import { useState } from 'react';
import { theme } from '@/lib/theme';
import type { AvailabilityRow, BlockedSlot } from '@/types';

const DAY_LABELS = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];

interface AvailabilityCalendarProps {
  availability: AvailabilityRow[];
  blockedSlots: BlockedSlot[];
}

export default function AvailabilityCalendar({ availability, blockedSlots }: AvailabilityCalendarProps) {
  const [viewDate, setViewDate] = useState(new Date());

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);

  const workingDays = new Set(
    availability.filter((a) => a.is_working).map((a) => a.day_of_week)
  );

  const partialBlockedDates = new Set(
    blockedSlots.filter((b) => b.start_time !== null).map((b) => b.blocked_date)
  );
  const fullBlockedDates = new Set(
    blockedSlots.filter((b) => b.start_time === null).map((b) => b.blocked_date)
  );

  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const getDayStatus = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dow = new Date(year, month, day).getDay();
    const isPast = dateStr < todayStr;

    if (fullBlockedDates.has(dateStr)) return 'blocked';
    if (partialBlockedDates.has(dateStr)) return 'partial';
    if (!workingDays.has(dow)) return 'off';
    if (isPast) return 'past';
    return 'available';
  };

  const statusStyle = (status: string, isToday: boolean) => {
    const base = 'w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-default';
    if (isToday) return `${base} ring-2 ring-offset-1 ring-offset-black`;
    return base;
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'available': return { color: theme.colors.textPrimary, backgroundColor: 'rgba(255,255,255,0.06)' };
      case 'blocked': return { color: theme.colors.danger, backgroundColor: theme.colors.dangerMuted };
      case 'partial': return { color: '#fbbf24', backgroundColor: 'rgba(251,191,36,0.1)' };
      case 'off': return { color: theme.colors.textDisabled, backgroundColor: 'transparent' };
      case 'past': return { color: theme.colors.textDisabled, backgroundColor: 'transparent' };
      default: return {};
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={prevMonth}
          className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer hover:bg-white/8 transition-colors"
          style={{ color: theme.colors.textMuted }}
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <p className="text-white text-sm font-semibold">
          {viewDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
        </p>
        <button
          onClick={nextMonth}
          className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer hover:bg-white/8 transition-colors"
          style={{ color: theme.colors.textMuted }}
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 mb-2">
        {DAY_LABELS.map((d) => (
          <div key={d} className="flex items-center justify-center">
            <span className="text-xs font-medium" style={{ color: theme.colors.textDisabled }}>{d}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isToday = dateStr === todayStr;
          const status = getDayStatus(day);
          const colors = statusColor(status);

          return (
            <div key={dateStr} className="flex items-center justify-center py-0.5">
              <div
                className={statusStyle(status, isToday)}
                style={{
                  ...colors,
                  ...(isToday ? { ringColor: theme.colors.primary } : {}),
                }}
              >
                {day}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-5 pt-4" style={{ borderTop: `1px solid ${theme.colors.border}` }}>
        {[
          { label: 'Disponible', color: theme.colors.textPrimary, bg: 'rgba(255,255,255,0.06)' },
          { label: 'Bloqueado', color: theme.colors.danger, bg: theme.colors.dangerMuted },
          { label: 'Bloqueo parcial', color: '#fbbf24', bg: 'rgba(251,191,36,0.1)' },
          { label: 'Día libre', color: theme.colors.textDisabled, bg: 'transparent' },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{
                backgroundColor: item.bg === 'transparent' ? theme.colors.border : item.bg,
                border: item.bg === 'transparent' ? `1px solid ${theme.colors.border}` : 'none',
              }}
            />
            <span className="text-xs" style={{ color: theme.colors.textMuted }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
